import axios, { CancelTokenSource } from 'axios';
import * as Routes from '@/modules/routes';
import {
  GameFilter,
  GameFilterField,
  GameFilterParams,
  GameInfo,
} from './types';

export const initialFilters: GameFilter = {
  category: [
    { checked: true, label: 'Lesson Review Game', value: 'lesson_review_game' },
    { checked: true, label: 'Vocabulary Game', value: 'vocabulary_game' },
  ],
  status: [
    { checked: false, label: 'Assigned', value: 'Assigned' },
    { checked: false, label: 'Unassigned', value: 'Unassigned' },
  ],
};

let filterCancelToken: CancelTokenSource;
export const getFilteredGames = (
  filters: GameFilter,
  programId: number | string,
) => {
  if (filterCancelToken !== undefined) {
    filterCancelToken.cancel('Operation canceled due to new request.');
  }
  filterCancelToken = axios.CancelToken.source();

  const params = {
    column: 'title_en',
    order: 'asc',
    page: 1,
    per_page: 50,
    'search[category_in]': [],
    'search[lesson_ids_in]': [],
    'search[program_id_eq]': programId,
  } as GameFilterParams;

  filters.unit?.forEach((unitFilters) => {
    const checkedLessons = unitFilters.lessons
      .filter(lessonFilter => lessonFilter.checked)
      .map(lessonFilter => lessonFilter.value);
    params['search[lesson_ids_in]'].push(...checkedLessons);
  });

  filters.lesson?.forEach((lessonFilter) => {
    if (lessonFilter.checked) {
      params['search[lesson_ids_in]'].push(lessonFilter.value);
    }
  });

  filters.category.forEach((categoryFilter) => {
    if (categoryFilter.checked) {
      params['search[category_in]'].push(categoryFilter.value);
    }
  });

  if (filters.status.some(statusFilter => statusFilter.checked && statusFilter.value === 'Assigned')) {
    params['search[assigned_eq]'] = true;
  }

  if (filters.status.some(statusFilter => statusFilter.checked && statusFilter.value === 'Unassigned')) {
    if (params['search[assigned_eq]'] === true) {
      params['search[assigned_eq]'] = undefined;
    } else {
      params['search[assigned_eq]'] = false;
    }
  }

  return axios({
    cancelToken: filterCancelToken.token,
    method: 'get',
    params,
    url: Routes.plato_api_games_path(),
  }).then(res => res.data.data as Array<GameInfo>);
};

export const getFilters = (programId: number | string, lessonId?: number | string, unitId?: number | string) => {
  const filters = { ...initialFilters };

  const savedFilters = (lessonId || unitId) ? initialFilters : JSON.parse(
    localStorage.getItem('game-filters') ?? JSON.stringify(initialFilters),
  ) as GameFilter;

  const lessonIsChecked = (lessonFilter: GameFilterField) => (
    lessonFilter.checked
    || savedFilters.unit?.some(unitFilter => (
      unitFilter.lessons.some(savedLesson => (
        savedLesson.value === lessonFilter.value && savedLesson.checked
      ))
    ))
  );

  const getUnitFilters = (filterData: GameFilter) => (
    filterData.unit?.map((unit) => {
      const label = Object.keys(unit)[0];
      const lessons = Object.values(unit)[0].map((lessonFilter: GameFilterField) => ({
        ...lessonFilter,
        checked: lessonIsChecked(lessonFilter),
      }));
      return { label, lessons };
    })
  );

  const getLessonFilters = (filterData: GameFilter) => (
    filterData.lesson?.map((lessonFilter) => {
      const checked = lessonFilter.checked || savedFilters.lesson?.some(savedLesson => (
        savedLesson.value === lessonFilter.value && savedLesson.checked
      ));

      return { ...lessonFilter, checked };
    })
  );

  const filterCategoriesPath = Routes.plato_api_filter_categories_games_path({
    lesson_id: lessonId,
    program_id: programId,
    unit_id: unitId,
  });

  return axios
    .get<GameFilter>(filterCategoriesPath)
    .then((res) => {
      const filterData = res.data;

      filters.unit = getUnitFilters(filterData);

      filters.lesson = getLessonFilters(filterData);

      filters.category = filterData.category.map((categoryFilter) => {
        const checked = savedFilters.category?.some(
          savedCategory => (savedCategory.value === categoryFilter.value && savedCategory.checked),
        );

        return { ...categoryFilter, checked };
      });

      filters.status = filters.status.map((statusFilter) => {
        const checked = savedFilters.status?.some(
          savedStatus => (savedStatus.value === statusFilter.value && savedStatus.checked),
        );
        return { ...statusFilter, checked };
      });

      return filters;
    });
};
